import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  Grid,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import ApiFetch from "../../services/api.fetch";
import { useTranslation } from "react-i18next";
import { useUserAuth } from "../../context/UserAuthContext";
import { useFiles } from "../../hooks/files/useFIles";
import { Profile, Document, FileToUpload, PresignedUrlResponse } from "./types";
import ProfileHeader from "./components/ProfileHeader";
import PersonalInfoForm from "./components/PersonalInfoForm";
import AddressInfoForm from "./components/AddressInfoForm";
import ScreeningTypeSelector from "./components/ScreeningTypeSelector";
import DocumentUploader from "./components/DocumentUploader";
import DocumentViewer from "../../submodules/document-uploader/DocumentViewer";

const resizeImage = (file: File): Promise<File> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      const MAX_WIDTH = 1920;
      const MAX_HEIGHT = 1080;

      let width = img.width;
      let height = img.height;

      if (width > height) {
        if (width > MAX_WIDTH) {
          height = Math.round((height * MAX_WIDTH) / width);
          width = MAX_WIDTH;
        }
      } else {
        if (height > MAX_HEIGHT) {
          width = Math.round((width * MAX_HEIGHT) / height);
          height = MAX_HEIGHT;
        }
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Could not get canvas context"));
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error("Could not create blob"));
          return;
        }

        const resizedFile = new File([blob], file.name, {
          type: file.type,
          lastModified: file.lastModified,
        });

        resolve(resizedFile);
      }, file.type);
    };

    img.onerror = () => {
      reject(new Error("Could not load image"));
    };
  });
};

const ScreeningProfile: React.FC = () => {
  const { profileId } = useParams<{ profileId: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useUserAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isCreateMode = profileId === "create";
  const [filesToUpload, setFilesToUpload] = useState<FileToUpload[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(
    null
  );
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const { getDownloadLinks } = useFiles();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingEmploymentType, setPendingEmploymentType] = useState<
    string | null
  >(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        if (isCreateMode) {
          setProfile({
            tenantId: user?.tenant?.id || 0,
            employmentType: "EMPLOYEE",
            screeningType: "RENTER",
            firstname: "",
            initials: "",
            surname: "",
            birthdate: "",
            birthplace: "",
            province: "",
            street: "",
            houseNumber: "",
            postalCode: "",
            city: "",
            country: "",
            phoneNumber: "",
            email: "",
            income: 0,
          });
        } else {
          const response = await ApiFetch.httpGet(
            `v1/tenant/profile/profileById/${profileId}`
          );
          setProfile(response.profile);
          setDocuments(response.documents || []);
        }
        setLoading(false);
      } catch (err) {
        setError("Failed to load profile");
        setLoading(false);
      }
    };

    fetchProfile();
  }, [profileId, isCreateMode, user?.tenant?.id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile) return;

    try {
      const requestBody = {
        tenantId: profile.tenantId,
        employmentType: profile.employmentType,
        screeningType: profile.screeningType,
        firstname: profile.firstname,
        initials: profile.initials,
        surname: profile.surname,
        birthdate: profile.birthdate,
        birthplace: profile.birthplace,
        province: profile.province,
        street: profile.street,
        houseNumber: profile.houseNumber,
        postalCode: profile.postalCode,
        city: profile.city,
        country: profile.country,
        phoneNumber: profile.phoneNumber,
        email: profile.email,
        income: profile.income,
      };

      if (isCreateMode) {
        await ApiFetch.httpPost("v1/tenant/profile/createProfile", requestBody);
      } else {
        await ApiFetch.httpPost("v1/tenant/profile/updateProfile", {
          ...requestBody,
          id: profile.id,
        });
      }
      navigate(-1);
    } catch (err) {
      setError(
        isCreateMode ? "Failed to create profile" : "Failed to update profile"
      );
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!profile) return;
    setProfile({
      ...profile,
      [e.target.name]: e.target.value,
    });
  };

  const handleScreeningTypeChange = (
    event: React.MouseEvent<HTMLElement>,
    newScreeningType: string
  ) => {
    if (newScreeningType !== null && profile) {
      setProfile({ ...profile, screeningType: newScreeningType });
    }
  };

  const handleEmploymentTypeChange = async (
    event: React.MouseEvent<HTMLElement>,
    newEmploymentType: string
  ) => {
    if (newEmploymentType !== null && profile) {
      if (!isCreateMode && documents.length > 0) {
        setPendingEmploymentType(newEmploymentType);
        setConfirmDialogOpen(true);
        return;
      }

      const updatedProfile = { ...profile, employmentType: newEmploymentType };
      setProfile(updatedProfile);

      if (!isCreateMode) {
        try {
          await ApiFetch.httpPost("v1/tenant/profile/updateProfile", {
            ...updatedProfile,
            id: profile.id,
          });
        } catch (err) {
          setError("Failed to update employment type");
          setProfile(profile);
        }
      }
    }
  };

  const handleConfirmEmploymentTypeChange = async () => {
    if (!profile || !pendingEmploymentType) return;

    try {
      const documentIds = documents.map((doc) => doc.fileId);
      if (documentIds.length > 0) {
        await ApiFetch.httpDelete(
          "v1/files/deleteProfileDocuments",
          documentIds
        );
      }

      const updatedProfile = {
        ...profile,
        employmentType: pendingEmploymentType,
      };
      setProfile(updatedProfile);
      setDocuments([]);

      if (!isCreateMode) {
        await ApiFetch.httpPost("v1/tenant/profile/updateProfile", {
          ...updatedProfile,
          id: profile.id,
        });
      }
    } catch (err) {
      setError("Failed to update employment type");
    } finally {
      setConfirmDialogOpen(false);
      setPendingEmploymentType(null);
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    documentType: string
  ) => {
    if (event.target.files && event.target.files[0]) {
      let file = event.target.files[0];
      const fileType = file.type.startsWith("image/") ? "IMAGE" : "DOCUMENT";
      if (fileType === "IMAGE") {
        try {
          file = await resizeImage(file);
        } catch (error) {
          console.error("Error resizing image:", error);
        }
      }
      const newFileToUpload = {
        fileName: file.name,
        fileType,
        documentType,
        file,
      };
      setFilesToUpload((prev) => [...prev, newFileToUpload]);
      try {
        const existingDoc = documents.find(
          (doc) => doc.screeningDocumentType === documentType
        );
        if (existingDoc) {
          try {
            await ApiFetch.httpDelete("/v1/files/deleteProfileDocuments", [
              existingDoc.fileId,
            ]);
          } catch (error) {
            console.error("Error deleting old document:", error);
          }
        }
        const prepareResponse = (await ApiFetch.httpPost(
          `v1/files/prepareProfileFiles/${user?.id}/TENANT_MICROSERVICE/${profileId}/PRIVATE_FILE`,
          [
            {
              fileName: file.name,
              fileType,
              documentType,
            },
          ]
        )) as PresignedUrlResponse[];
        if (prepareResponse.length > 0) {
          const presignedUrl = prepareResponse[0].presignedUrl;
          await fetch(presignedUrl, {
            method: "PUT",
            body: file,
            headers: {
              "Content-Type": file.type,
            },
          });
          setFilesToUpload((prev) =>
            prev.filter(
              (item) =>
                !(
                  item.documentType === documentType &&
                  item.fileName === file.name
                )
            )
          );
          if (!isCreateMode) {
            const response = await ApiFetch.httpGet(
              `v1/tenant/profile/profileById/${profileId}`
            );
            setDocuments(response.documents || []);
          }
        }
      } catch (error) {
        console.error("Error handling file upload:", error);
      }
    }
  };

  const removeFile = (index: number) => {
    setFilesToUpload((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadFiles = async () => {
    if (filesToUpload.length === 0) return;
    try {
      const prepareResponse = (await ApiFetch.httpPost(
        `v1/files/prepareProfileFiles/${user?.id}/TENANT_MICROSERVICE/${profileId}/PRIVATE_FILE`,
        filesToUpload.map(({ fileName, fileType, documentType }) => ({
          fileName,
          fileType,
          documentType,
        }))
      )) as PresignedUrlResponse[];
      for (let i = 0; i < filesToUpload.length; i++) {
        const file = filesToUpload[i];
        const presignedUrl = prepareResponse[i].presignedUrl;
        await fetch(presignedUrl, {
          method: "PUT",
          body: file.file,
          headers: {
            "Content-Type": file.file.type,
          },
        });
        const existingDoc = documents.find(
          (doc) => doc.screeningDocumentType === file.documentType
        );
        if (existingDoc) {
          try {
            await ApiFetch.httpDelete("/v1/files/deleteProfileDocuments", [
              existingDoc?.fileId,
            ]);
          } catch (error) {
            console.error("Error deleting old document:", error);
          }
        }
      }
      setFilesToUpload([]);
      if (!isCreateMode) {
        const response = await ApiFetch.httpGet(
          `v1/tenant/profile/profileById/${profileId}`
        );
        setDocuments(response.documents || []);
      }
    } catch (error) {
      console.error("Error uploading files:", error);
    }
  };

  const handleViewDocument = async (document: Document) => {
    try {
      const links = await getDownloadLinks([document.fileId]);
      if (links.length > 0) {
        setDocumentUrl(links[0].presignedUrl);
        setSelectedDocument(document);
        setOpenModal(true);
      }
    } catch (error) {
      console.error("Error fetching document link:", error);
    }
  };

  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Container maxWidth="lg">
      <Box>
        <Paper sx={{ p: 3, minHeight: "45rem" }}>
          <ProfileHeader isCreateMode={isCreateMode} />
          {profile && (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <PersonalInfoForm
                  profile={profile}
                  handleChange={handleChange}
                />
                <AddressInfoForm
                  profile={profile}
                  handleChange={handleChange}
                />
                <Grid item xs={12}>
                  <ScreeningTypeSelector
                    profile={profile}
                    handleScreeningTypeChange={handleScreeningTypeChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <DocumentUploader
                    documents={documents}
                    filesToUpload={filesToUpload}
                    handleFileChange={handleFileChange}
                    removeFile={removeFile}
                    uploadFiles={uploadFiles}
                    handleViewDocument={handleViewDocument}
                    employmentType={profile.employmentType}
                    profile={profile}
                    handleEmploymentTypeChange={handleEmploymentTypeChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="secondary"
                    sx={{
                      mt: 2,
                      color: "#fff",
                    }}
                  >
                    {isCreateMode
                      ? t("screening_profile.add_profile")
                      : t("screening_profile.save_changes")}
                  </Button>
                </Grid>
              </Grid>
            </form>
          )}
        </Paper>
      </Box>

      <DocumentViewer
        openModal={openModal}
        setOpenModal={setOpenModal}
        selectedDocument={selectedDocument}
        documentUrl={documentUrl}
      />

      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
      >
        <DialogTitle>
          {t("screening_profile.change_employment_type_warning_title")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("screening_profile.change_employment_type_warning_message")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleConfirmEmploymentTypeChange}
            color="error"
            autoFocus
          >
            {t("common.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ScreeningProfile;
